import { useEffect, useId, useRef } from 'react';
import styles from './Modal.module.css';

// Shared modal: replaces the 3 divergent implementations found in the audit
// (Customer.js's React-state modal, MFAGuard.js's inline-style-only dialog,
// and Invoices.js/AddInvoice.js's raw document.createElement popups).
export default function Modal({ open, onClose, title, children, footer, size, flush }) {
  const titleId = useId();
  const contentRef = useRef(null);

  // Callers pass an inline onClose (a new function every render). If it were
  // an effect dependency, every keystroke in a form field would re-run the
  // effect and re-focus the dialog, stealing focus from the input after one
  // character. So the latest onClose lives in a ref and the effect depends
  // on `open` alone.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', handleKey);
    // Moves focus into the dialog once, on open, so keyboard/screen-reader
    // users aren't left focused on a trigger button that's now behind the
    // overlay.
    contentRef.current?.focus();
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  if (!open) return null;

  const contentClass = [
    styles.content,
    size === 'large' ? styles.contentLarge : '',
    flush ? styles.contentFlush : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        ref={contentRef}
        className={contentClass}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        <div className={styles.header}>
          {title && <h3 className={styles.title} id={titleId}>{title}</h3>}
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
}
